// articles_manager.jsx
// Manage the scraped cruise-news feed (news_articles): list + filter + edit +
// delete, and toggle the is_duplicate flag the scraper sets on near-identical
// (syndicated) headlines. Matches the live navela-news API: PATCH partial update
// (also marks/unmarks a duplicate), 204 delete. There is no create — articles are
// born from the scraper, not the dashboard.

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Newspaper, Search, Pencil, Trash2, RefreshCw, ImageOff, Copy, Undo2,
  ChevronLeft, ChevronRight, ExternalLink, CopyCheck,
} from 'lucide-react';
import { useNavela } from '../../authentication_components/navelaContext';
import { Modal, Field, inputClass, Button, Loading, EmptyState, ConfirmDialog, strOrNull, numOrNull } from './navela_ui';

const PAGE_SIZE = 25;

// datetime-local <-> ISO helpers. The input works in the admin's local time; the
// API stores/returns RFC3339, so we convert on the way in and out.
const pad = (n) => String(n).padStart(2, '0');
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const EMPTY_FORM = {
  title: '', summary: '', author: '', url: '', thumbnail_url: '', published_at: '',
  relevance_score: '', cruise_line_id: '', vessel_id: '',
  is_published: true, is_duplicate: false, duplicate_of: '',
};

function toForm(a) {
  return {
    title: a.title ?? '',
    summary: a.summary ?? '',
    author: a.author ?? '',
    url: a.url ?? '',
    thumbnail_url: a.thumbnail_url ?? '',
    published_at: toLocalInput(a.published_at),
    relevance_score: a.relevance_score ?? '',
    cruise_line_id: a.cruise_line_id ?? '',
    vessel_id: a.vessel_id ?? '',
    is_published: a.is_published ?? true,
    is_duplicate: a.is_duplicate ?? false,
    duplicate_of: a.duplicate_of ?? '',
  };
}

function toPayload(form) {
  const payload = {
    title: form.title.trim(),
    summary: strOrNull(form.summary),
    author: strOrNull(form.author),
    url: form.url.trim(),
    thumbnail_url: strOrNull(form.thumbnail_url),
    relevance_score: numOrNull(form.relevance_score),
    cruise_line_id: numOrNull(form.cruise_line_id),
    vessel_id: numOrNull(form.vessel_id),
    is_published: !!form.is_published,
    is_duplicate: !!form.is_duplicate,
  };
  const iso = fromLocalInput(form.published_at);
  if (iso) payload.published_at = iso;
  // Only send a duplicate target when the row is flagged and a value is present;
  // unflagging clears the link server-side.
  if (form.is_duplicate) {
    const dup = numOrNull(form.duplicate_of);
    if (dup) payload.duplicate_of = dup;
  }
  return payload;
}

function Thumb({ url, alt }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) {
    return (
      <div className="flex h-11 w-16 items-center justify-center rounded-md bg-slate-100 text-slate-300">
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }
  return <img src={url} alt={alt} onError={() => setBroken(true)} className="h-11 w-16 rounded-md object-cover ring-1 ring-slate-200" loading="lazy" />;
}

export default function ArticlesManager() {
  const { fetchArticles, updateArticle, setArticleDuplicate, deleteArticle, fetchArticleStats, fetchNewsSources, loading } = useNavela();

  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [duplicateFilter, setDuplicateFilter] = useState(''); // '', 'true', 'false'
  const [publishedFilter, setPublishedFilter] = useState(''); // '', 'true', 'false'
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [dupBusyId, setDupBusyId] = useState(null);

  const load = useCallback(async (opts = {}) => {
    try {
      const data = await fetchArticles({
        search: search || undefined,
        source_id: sourceId || undefined,
        is_duplicate: duplicateFilter || undefined,
        is_published: publishedFilter || undefined,
        limit: PAGE_SIZE,
        offset: opts.offset ?? offset,
      });
      setArticles(data?.articles ?? []);
      setTotal(data?.total_count ?? 0);
    } catch (err) {
      toast.error(err.message || 'Failed to load articles');
    }
  }, [fetchArticles, search, sourceId, duplicateFilter, publishedFilter, offset]);

  const refreshStats = useCallback(() => {
    fetchArticleStats().then(setStats).catch(() => {});
  }, [fetchArticleStats]);

  // Source picker options + stats (once).
  useEffect(() => {
    fetchNewsSources().then((d) => setSources(d?.sources ?? [])).catch(() => {});
    refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); load({ offset: 0 }); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sourceId, duplicateFilter, publishedFilter]);

  useEffect(() => {
    load({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (a) => { setForm(toForm(a)); setEditing(a); };
  const closeModal = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.url.trim()) { toast.error('URL is required'); return; }
    setSaving(true);
    try {
      await updateArticle(editing.id, toPayload(form));
      toast.success('Article updated');
      closeModal();
      load();
      refreshStats();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleDuplicate = async (a) => {
    setDupBusyId(a.id);
    try {
      await setArticleDuplicate(a.id, !a.is_duplicate);
      toast.success(a.is_duplicate ? 'Marked as original (shown in app)' : 'Marked as duplicate (hidden from app)');
      load();
      refreshStats();
    } catch (err) {
      toast.error(err.message || 'Failed to update article');
    } finally {
      setDupBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteArticle(deleteTarget.id);
      toast.success('Article deleted');
      setDeleteTarget(null);
      const nextOffset = articles.length === 1 && offset >= PAGE_SIZE ? offset - PAGE_SIZE : offset;
      setOffset(nextOffset);
      load({ offset: nextOffset });
      refreshStats();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const go = (nextOffset) => { setOffset(nextOffset); load({ offset: nextOffset }); };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

  const isEmpty = !loading && articles.length === 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <Newspaper className="h-5 w-5 text-pr-navy-600" /> News Articles
          </h2>
          <p className="text-sm text-slate-500">
            {stats
              ? `${stats.total_articles.toLocaleString()} articles · ${stats.published.toLocaleString()} live · ${stats.duplicates.toLocaleString()} duplicates`
              : `${total.toLocaleString()} article${total === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => { load(); refreshStats(); }} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, summary or author…" className={`${inputClass} pl-9`} />
        </div>
        <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} className={`${inputClass} w-52`}>
          <option value="">Any source</option>
          {sources.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
        </select>
        <select value={duplicateFilter} onChange={(e) => setDuplicateFilter(e.target.value)} className={`${inputClass} w-44`}>
          <option value="">All (dupes + originals)</option>
          <option value="false">Originals only</option>
          <option value="true">Duplicates only</option>
        </select>
        <select value={publishedFilter} onChange={(e) => setPublishedFilter(e.target.value)} className={`${inputClass} w-40`}>
          <option value="">Any status</option>
          <option value="true">Published</option>
          <option value="false">Hidden</option>
        </select>
      </div>

      {loading && articles.length === 0 ? (
        <Loading label="Loading articles…" />
      ) : isEmpty ? (
        <EmptyState icon={Newspaper} title="No articles match" hint="Adjust the filters. Articles are added by the scraper, not here." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Article</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Published</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {articles.map((a) => (
                  <tr key={a.id} className={`hover:bg-slate-50/60 ${a.is_duplicate ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Thumb url={a.thumbnail_url} alt={a.title} />
                        <div className="min-w-0 max-w-md">
                          <div className="truncate font-semibold text-slate-800" title={a.title}>{a.title}</div>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span>#{a.id}</span>
                            {a.url ? (
                              <a href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-pr-navy-500 hover:text-pr-navy-700" title="Open source article">
                                source <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : null}
                            {a.duplicate_of ? <span className="text-amber-600">dup of #{a.duplicate_of}</span> : null}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{a.source || '—'}{a.cruise_line ? <div className="text-xs text-slate-400">{a.cruise_line}</div> : null}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(a.published_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {a.is_duplicate ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Duplicate</span>
                        ) : a.is_published ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Published</span>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Hidden</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleDuplicate(a)}
                          disabled={dupBusyId === a.id}
                          className={`rounded-lg p-2 disabled:opacity-40 ${a.is_duplicate ? 'text-amber-600 hover:bg-amber-50' : 'text-slate-400 hover:bg-amber-50 hover:text-amber-600'}`}
                          title={a.is_duplicate ? 'Unmark duplicate (show in app)' : 'Mark as duplicate (hide from app)'}
                        >
                          {a.is_duplicate ? <Undo2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </button>
                        <button onClick={() => openEdit(a)} className="rounded-lg p-2 text-slate-400 hover:bg-pr-navy-50 hover:text-pr-navy-600" title="Edit"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => setDeleteTarget(a)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
            <span>Page {page} of {pageCount}</span>
            <div className="flex items-center gap-1">
              <button disabled={offset === 0} onClick={() => go(Math.max(0, offset - PAGE_SIZE))} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
              <button disabled={page >= pageCount} onClick={() => go(offset + PAGE_SIZE)} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={editing !== null}
        title="Edit article"
        subtitle={editing ? `#${editing.id} · ${editing.source || 'news'}` : ''}
        onClose={closeModal}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
          </>
        }
      >
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Title" required className="sm:col-span-2">
            <input className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Summary" className="sm:col-span-2" hint="Shown under the headline in the feed">
            <textarea rows={3} className={inputClass} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
          </Field>
          <Field label="Author">
            <input className={inputClass} value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
          </Field>
          <Field label="Published at" hint="Feed is date-ordered; required to appear">
            <input type="datetime-local" className={inputClass} value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} />
          </Field>
          <Field label="Article URL" required className="sm:col-span-2" hint="The canonical link the card opens">
            <input className={inputClass} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" />
          </Field>
          <Field label="Thumbnail URL" className="sm:col-span-2">
            <input className={inputClass} value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="https://…" />
          </Field>
          <Field label="Relevance score" hint="Higher ranks earlier">
            <input type="number" step="any" className={inputClass} value={form.relevance_score} onChange={(e) => setForm({ ...form, relevance_score: e.target.value })} />
          </Field>
          <Field label="Cruise line id" hint="Numeric id, or blank to clear">
            <input type="number" min="0" className={inputClass} value={form.cruise_line_id} onChange={(e) => setForm({ ...form, cruise_line_id: e.target.value })} />
          </Field>
          <Field label="Vessel id" hint="Numeric id, or blank to clear">
            <input type="number" min="0" className={inputClass} value={form.vessel_id} onChange={(e) => setForm({ ...form, vessel_id: e.target.value })} />
          </Field>
          <div className="sm:col-span-2 grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-pr-navy-600 focus:ring-pr-navy-300" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
              Published (visible in the app)
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-300" checked={form.is_duplicate} onChange={(e) => setForm({ ...form, is_duplicate: e.target.checked })} />
              <span className="inline-flex items-center gap-1"><CopyCheck className="h-4 w-4 text-amber-500" /> Duplicate (hidden from app)</span>
            </label>
            {form.is_duplicate && (
              <Field label="Duplicate of (article id)" className="sm:col-span-2" hint="Optional — the original this repeats">
                <input type="number" min="0" className={inputClass} value={form.duplicate_of} onChange={(e) => setForm({ ...form, duplicate_of: e.target.value })} />
              </Field>
            )}
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete article?"
        message={deleteTarget ? `“${deleteTarget.title}” will be permanently removed from the database. This cannot be undone. To just hide it from the app, mark it a duplicate instead.` : ''}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400">
        <Copy className="h-3.5 w-3.5" /> The scraper auto-flags near-identical headlines as duplicates. Marking a duplicate hides it from the app &amp; webapp but keeps it here.
      </p>
    </div>
  );
}
